import { Button, Grid } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import ManageRelatedProductsButton from "../../components/button/ManageRelatedProductsButton";
import { InvestmentFilter } from "../../components/common/InvestmentFilter";
import AddEditDocsRestrictedDialog from "../../components/dialog/AddEditDocsRestrictedDialog";
import FileTableCell from "../../components/other/FileTableCell";
import { FilterTableWrapper } from "../../components/table/BetterFilterTable/BetterFilterTable";
import DefaultPageWrapper from "../../components/wrapper/DefaultPageWrapper";
import { useAuth } from "../../context/auth";
import { DOCS_RESTRICTED_ROLE, DOCS_RESTRICTED_USER, USER_ROLES } from "../../helpers/constants";
import { useAccessControl } from "../../hooks/auth/useCheckPermission";
import { useAxiosPrivate } from "../../hooks/useAxiosPrivate";
import usePaginationWithSearchParams from "../../hooks/usePaginationWithSearchParams";
import DateService from "../../services/dateService";
import {
    documentsApi,
    useAllProductDocumentsWithDetails,
    useDeleteDocumentFromAllProducts,
    useDocsFilteringData,
} from "../../services/documentsService";
import { useGetSellers } from "../../services/usersApi";
import { NoAccessPage } from "../NoAccessPage/NoAccessPage";
import DocumentsNavigationBar from "./DocumentsNavigationBar";
import { SelectProductsForDocumentDialog } from "./SelectProductsForDocumentDialog";
import { BOOLEAN_FIELD_TYPE } from "../../helpers/constants";
import SelectFieldService from "../../services/selectFieldService";

export const DocumentsPage = (props) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const axiosPrivate = useAxiosPrivate();

    //---- Data for filtering
    const { data: filteringData } = useDocsFilteringData();

    const userFilterOptions = useMemo(
        () =>
            filteringData?.docs_restricted_user.map((u) => ({
                value: u.id,
                label: u.full_name,
            })) || [],
        [filteringData],
    );

    const roleFilterOptions = useMemo(
        () =>
            filteringData?.docs_restricted_role.map((r) => ({
                value: r.id,
                label: r.role_name,
            })) || [],
        [filteringData],
    );

    const industryFilterOptions = useMemo(
        () =>
            filteringData?.industries.map((ind) => ({
                value: ind.id,
                label: ind.industry_name,
            })) || [],
        [filteringData],
    );

    const { data: sellers } = useGetSellers();
    const sellerFilterOptions = useMemo(
        () =>
            sellers?.map((s) => ({
                value: s.username,
                label: s.full_name,
            })) || [],
        [sellers],
    );

    //---- Fetching documents
    const [documentsLocal, setDocumentsLocal] = useState([]);
    const { searchParams, setSearchParams } = usePaginationWithSearchParams();

    const { data: documentsData, refetch: refetchDocuments, isLoading } =
        useAllProductDocumentsWithDetails(searchParams);

    // Remember the last response so that table doesn't flash empty on refetch
    useEffect(() => {
        if (documentsData != null) {
            setDocumentsLocal(documentsData);
        }
    }, [documentsData]);

    //---- Table button handlers
    const deleteMutation = useDeleteDocumentFromAllProducts({
        onSuccess: () => {
            toast.success(t("documents.document_deleted_successfully"));
            refetchDocuments();
        },
        onError: () => {
            toast.error(t("snackbar_alert.occurred_error_during_delete"));
        },
    });

    const handlePreviewDoc = useCallback(
        async (docId) => {
            try {
                await documentsApi.handlePreviewDocumentFromLink(axiosPrivate, docId);
            } catch (error) {
                toast.error(t("snackbar_alert.occurred_error_during_preview"));
            }
        },
        [axiosPrivate, t],
    );

    const handleDownloadDoc = useCallback(
        async (docId) => {
            try {
                await documentsApi.handleDownloadDocumentFromLink(axiosPrivate, docId);
            } catch (error) {
                toast.error(t("snackbar_alert.occurred_error_during_download"));
            }
        },
        [axiosPrivate, t],
    );

    const handleDeleteFile = useCallback(
        (fileId) => {
            deleteMutation.mutate({ document_id: fileId });
        },
        [deleteMutation],
    );

    //---- Dialogs
    const [selectedProductIds, setSelectedProductIds] = useState([]);
    const [productSelectionDialogOpen, setProductSelectionDialogOpen] = useState(false);
    const [addDocumentDialogOpen, setAddDocumentDialogOpen] = useState(false);

    const handleAddFile = () => {
        setProductSelectionDialogOpen(true);
    };

    const handleProductSelectionConfirm = useCallback(
        (productIds) => {
            setSelectedProductIds(productIds);
            setProductSelectionDialogOpen(false);
            setAddDocumentDialogOpen(true);
        },
        [setSelectedProductIds, setProductSelectionDialogOpen, setAddDocumentDialogOpen],
    );

    const handleProductSelectionClose = useCallback(() => {
        setProductSelectionDialogOpen(false);
    }, [setProductSelectionDialogOpen]);

    const handleAddDocumentDialogClose = useCallback(() => {
        setAddDocumentDialogOpen(false);
        setSelectedProductIds([]);
    }, [setAddDocumentDialogOpen, setSelectedProductIds]);

    const handleAddDocumentSubmit = useCallback(
        (_uploadedDocIds) => {
            toast.success(t("documents.documents_uploaded_successfully"));
            refetchDocuments();
            setAddDocumentDialogOpen(false);
            setSelectedProductIds([]);
        },
        [refetchDocuments, setAddDocumentDialogOpen, setSelectedProductIds, t],
    );

    const { hasAccess } = useAccessControl([USER_ROLES.ADMIN, USER_ROLES.SALES_MANAGER]);
    if (!hasAccess) {
        return <NoAccessPage />;
    }

    return (
        <DefaultPageWrapper tagTitle={t("headers.documents")} titleKey={"documents"}>
            <Grid
                item
                container
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
                rowSpacing={1}
            >
                <Grid item xs={12}>
                    <DocumentsNavigationBar multipleLocationKey={"/sales-manager/documents/"} />
                </Grid>
                <Grid item container direction="row" alignItems="center" spacing={3}>
                    <Grid item xs={2}>
                        <InvestmentFilter
                            searchParams={searchParams}
                            setSearchParams={setSearchParams}
                        />
                    </Grid>
                    <Grid item xs={10}>
                        <Grid container spacing={2} justifyContent="flex-end">
                            <Grid item>
                                <Button variant="contained" onClick={handleAddFile}>
                                    {t("documents.DocumentsPage.addFile", "Add Document")}
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>

                <Grid item xs={12}>
                    <FilterTableWrapper
                        data={documentsLocal}
                        filtersOpts={{ showCleanFilterIcon: true }}
                        isLoading={isLoading}
                        fieldsConfig={[
                            {
                                getValueCallback: (doc) => (
                                    <ManageRelatedProductsButton
                                        initialSelectedProductIds={doc.related_products}
                                        documentData={doc}
                                    />
                                ),
                            },
                            {
                                name: "docs_nr",
                                getValueCallback: (doc) => doc.docs_nr,
                                label: t("table.docs_table.docs_nr"),
                                filterType: "text",
                            },
                            {
                                name: "docs_name",
                                getValueCallback: (doc) => doc.docs_name,
                                label: t("table.docs_table.docs_name"),
                                filterType: "text",
                            },
                            {
                                name: "docs_parent_nr",
                                getValueCallback: (doc) => doc.docs_parent_nr,
                                label: t("table.docs_table.docs_parent"),
                                filterType: "text",
                            },
                            {
                                name: "docs_revision_nr",
                                getValueCallback: (doc) => doc.docs_revision_nr,
                                label: t("table.docs_table.docs_revision_nr"),
                            },
                            {
                                name: "docs_industry",
                                getValueCallback: (doc) => doc.docs_industry,
                                label: t("table.docs_table.docs_industry"),
                                filterType: "select",
                                fieldConfig: {
                                    options: industryFilterOptions,
                                    optionKey: "value",
                                    optionLabel: "label",
                                },
                            },
                            {
                                name: "docs_restricted_user",
                                getValueCallback: (doc) => doc.docs_restricted_user,
                                label: t("table.docs_table.docs_restricted_user"),
                                filterType: "select",
                                fieldConfig: {
                                    options: userFilterOptions,
                                    optionKey: "value",
                                    optionLabel: "label",
                                },
                            },
                            {
                                name: "docs_restricted_role",
                                getValueCallback: (doc) => doc.docs_restricted_role,
                                label: t("table.docs_table.docs_restricted_role"),
                                filterType: "select",
                                fieldConfig: {
                                    options: roleFilterOptions,
                                    optionKey: "value",
                                    optionLabel: "label",
                                },
                            },
                            {
                                name: "created",
                                getValueCallback: (doc) =>
                                    DateService.convertDatetimeFromBackendToFormatYYYYMMDD(
                                        doc.created,
                                    ),
                                label: t("table.docs_table.created"),
                                filterType: "datetime_to_date_range",
                            },
                            {
                                name: "creator",
                                getValueCallback: (doc) => doc.creator,
                                label: t("table.docs_table.creator"),
                                filterType: "select",
                                fieldConfig: {
                                    options: sellerFilterOptions,
                                    optionKey: "value",
                                    optionLabel: "label",
                                },
                            },
                            {
                                name: "docsprod_is_card_pdf",
                                getValueCallback: (rowData) => SelectFieldService.getLabelOptionsTrueOrFalse(rowData.is_card_pdf),
                                label: t("table.docs_table.docsprod_is_card_pdf"),
                                filterType: BOOLEAN_FIELD_TYPE,
                                getFilterFieldConfigCallback: (filteringData) => undefined,
                            },
                            {
                                name: "docsprod_is_card_jpg",
                                getValueCallback: (rowData) => SelectFieldService.getLabelOptionsTrueOrFalse(rowData.is_card_jpg),
                                label: t("table.docs_table.docsprod_is_card_jpg"),
                                filterType: BOOLEAN_FIELD_TYPE,
                                getFilterFieldConfigCallback: (filteringData) => undefined,
                            },
                            {
                                name: "note",
                                getValueCallback: (doc) => doc.note,
                                label: t("table.docs_table.note"),
                                filterType: "text",
                            },
                            {
                                getValueCallback: (doc) => {
                                    return (
                                        <FileTableCell
                                            fileType={"docs"}
                                            file={doc}
                                            onPreview={(id, _) => handlePreviewDoc(id)}
                                            onDownload={(id, _) => handleDownloadDoc(id)}
                                            onDeleteFile={handleDeleteFile}
                                            readOnly={false}
                                            // @ts-ignore
                                            availableDocsRestrictedTypes={[DOCS_RESTRICTED_ROLE, DOCS_RESTRICTED_USER]}
                                            fileRefetch={refetchDocuments}
                                            index={0}
                                            showDetailsButton={false}
                                            showRevisionButton={true}
                                            showEditButton={true}
                                            isCardJpg={doc.is_card_jpg}
                                            isCardPdf={doc.is_card_pdf}
                                            isProspectus={doc.is_prospectus}
                                        />
                                    );
                                },
                            },
                        ]}
                    />
                </Grid>
            </Grid>
            <SelectProductsForDocumentDialog
                open={productSelectionDialogOpen}
                onClose={handleProductSelectionClose}
                onConfirm={handleProductSelectionConfirm}
            />
            <AddEditDocsRestrictedDialog
                open={addDocumentDialogOpen}
                onClose={handleAddDocumentDialogClose}
                onSubmit={handleAddDocumentSubmit}
                request_user={user?.username || ""}
                multiple={true}
                showIndustry={true}
                availableDocsRestrictedTypes={[DOCS_RESTRICTED_USER, DOCS_RESTRICTED_ROLE]}
                readOnly={false}
                filesOnMemory={false}
                isRevisionMode={false}
                product_ids={selectedProductIds}
            />
        </DefaultPageWrapper>
    );
};
